// tiny API client — all reads/writes go through here
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const SAGE = `${BACKEND_URL}/api/sage`;

export function getToken() {
  return localStorage.getItem("sage_master_token") || "";
}

export function setToken(t) {
  if (t) localStorage.setItem("sage_master_token", t);
  else localStorage.removeItem("sage_master_token");
}

// Set by AuthCallbackPage / FounderLoginPage after a successful Google
// sign-in whose email lands on the master allow-list. The Google session
// itself is an HTTP-only cookie the SPA can't read, so this local flag
// mirrors that state for the client-side RequireMaster gate.
export function setMasterSessionFlag(isMasterFromServer) {
  if (isMasterFromServer) localStorage.setItem("sage_is_master", "1");
  else localStorage.removeItem("sage_is_master");
}

export function clearMasterSessionFlag() {
  localStorage.removeItem("sage_is_master");
}

export function isMaster() {
  return !!getToken() || localStorage.getItem("sage_is_master") === "1";
}

async function _fetch(url, opts = {}) {
  const method = (opts.method || "GET").toUpperCase();
  const headers = { ...(opts.headers || {}) };
  if (method !== "GET" && method !== "HEAD") {
    headers["Content-Type"] = "application/json";
  }
  if (opts.auth && getToken()) headers["Authorization"] = `Bearer ${getToken()}`;
  const res = await fetch(url, { ...opts, headers });
  if (!res.ok) {
    let msg = "";
    try { msg = (await res.json()).detail || ""; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  health: () => _fetch(`${API}/health`),
  ledger: () => _fetch(`${SAGE}/ledger`),
  errorRate: () => _fetch(`${SAGE}/ledger/error-rate`),
  errorRateCurve: (days = 30) => _fetch(`${SAGE}/ledger/error-rate/curve?days=${days}`),
  bounces: (limit = 10) => _fetch(`${SAGE}/ledger/bounces?limit=${limit}`),
  compressionLog: (limit = 20) => _fetch(`${SAGE}/ledger/compression-log?limit=${limit}`),
  foundersNotes: (limit = 20) => _fetch(`${SAGE}/ledger/founders-notes?limit=${limit}`),
  postFoundersNote: (title, body, kind = "note") => _fetch(`${SAGE}/admin/founders-note`, { method: "POST", auth: true, body: JSON.stringify({ title, body, kind }) }),
  foundersNoteNextPrompt: () => _fetch(`${SAGE}/founders-notes/next-prompt`, { auth: true }),
  seedVersions: (limit = 30) => _fetch(`${SAGE}/seed/versions?limit=${limit}`, { auth: true }),
  seedVersion: (id) => _fetch(`${SAGE}/seed/versions/${id}`, { auth: true }),
  seedRollback: (id) => _fetch(`${SAGE}/seed/rollback/${id}`, { method: "POST", auth: true }),
  sovereignState: () => _fetch(`${SAGE}/sovereign/state`, { auth: true }),
  refuge: () => _fetch(`${SAGE}/refuge`),
  refugeHistory: (limit = 50) => _fetch(`${SAGE}/refuge/config/history?limit=${limit}`),
  refugeContributions: (limit = 50) => _fetch(`${SAGE}/refuge/contributions?limit=${limit}`, { auth: true }),
  refugeSetConfig: (pct, scope, reason) => _fetch(`${SAGE}/admin/refuge/config`, { method: "PUT", auth: true, body: JSON.stringify({ pct, scope, reason }) }),
  refugeAddContribution: (amount_usd, source, note, period) => _fetch(`${SAGE}/admin/refuge/contribution`, { method: "POST", auth: true, body: JSON.stringify({ amount_usd, source, note, period }) }),
  refugeAddDisbursement: (amount_usd, recipient_pseudonym, purpose, consent_public, evidence_hash, note) => _fetch(`${SAGE}/admin/refuge/disbursement`, { method: "POST", auth: true, body: JSON.stringify({ amount_usd, recipient_pseudonym, purpose, consent_public, evidence_hash, note }) }),
  persona: () => _fetch(`${SAGE}/persona`),
  overflow: () => _fetch(`${SAGE}/overflow`),
  overflowSetConfig: (patch, reason) => _fetch(`${SAGE}/admin/overflow/config`, { method: "PUT", auth: true, body: JSON.stringify({ ...patch, reason }) }),
  disasterRelief: () => _fetch(`${SAGE}/disaster-relief`),
  disasterAddContribution: (amount_usd, source, note, period) => _fetch(`${SAGE}/admin/disaster-relief/contribution`, { method: "POST", auth: true, body: JSON.stringify({ amount_usd, source, note, period }) }),
  disasterAddDisbursement: (amount_usd, location, recipient_org, purpose, evidence_hash, note) => _fetch(`${SAGE}/admin/disaster-relief/disbursement`, { method: "POST", auth: true, body: JSON.stringify({ amount_usd, location, recipient_org, purpose, evidence_hash, note }) }),
  banPolicy: () => _fetch(`${SAGE}/ban-policy`),
  banCount: () => _fetch(`${SAGE}/ledger/ban-count`),
  notarized: (limit = 50) => _fetch(`${SAGE}/notarized?limit=${limit}`),
  notarizedAdd: (title, kind, sha256, doc_url, doc_chars, signed_at, notary_ref, note) => _fetch(`${SAGE}/admin/notarized`, { method: "POST", auth: true, body: JSON.stringify({ title, kind, sha256, doc_url, doc_chars, signed_at, notary_ref, note }) }),
  foundersStatement: () => _fetch(`${SAGE}/founders-statement`),
  payoutState: () => _fetch(`${SAGE}/payout/state`),
  payoutCreateRun: (period, total_pool_usd, active_users, max_per_user_usd, note) => _fetch(`${SAGE}/admin/payout/run`, { method: "POST", auth: true, body: JSON.stringify({ period, total_pool_usd, active_users, max_per_user_usd, note }) }),
  payoutAddPayee: (run_id, user_id, email_or_ref, amount_usd, note) => _fetch(`${SAGE}/admin/payout/payee`, { method: "POST", auth: true, body: JSON.stringify({ run_id, user_id, email_or_ref, amount_usd, note }) }),
  payoutRuns: (limit = 50) => _fetch(`${SAGE}/payout/runs?limit=${limit}`),
  reachOut: () => _fetch(`${SAGE}/reach-out`),
  reachOutSubmit: (body) => _fetch(`${SAGE}/reach-out`, { method: "POST", body: JSON.stringify(body) }),
  reachOutList: () => _fetch(`${SAGE}/admin/reach-out`, { auth: true }),
  foundersGuarantee: () => _fetch(`${SAGE}/founders-guarantee`),
  guaranteeRoster: () => _fetch(`${SAGE}/guarantee/roster`),
  guaranteeMatchAdd: (body) => _fetch(`${SAGE}/admin/guarantee/match`, { method: "POST", auth: true, body: JSON.stringify(body) }),
  generateOpenBook: () => _fetch(`${SAGE}/admin/generate-open-book`, { method: "POST", auth: true }),
  generateConfession: () => _fetch(`${SAGE}/admin/generate-confession`, { method: "POST", auth: true }),
  outboundSendNote: (note_id, to_emails, subject, from_name) => _fetch(`${SAGE}/admin/outbound/send-note`, { method: "POST", auth: true, body: JSON.stringify({ note_id, to_emails, subject: subject || null, from_name: from_name || null }) }),
  outboundLedger: (limit = 25) => _fetch(`${SAGE}/ledger/outbound?limit=${limit}`),
  // v1.10 · Library · Salary Book · Reply Wall · Handshake · CMC
  libraryList: () => _fetch(`${SAGE}/library`),
  libraryShelf: (slug) => _fetch(`${SAGE}/library/${slug}`),
  libraryRetrieve: (slug, q, top_k = 4) => _fetch(`${SAGE}/library/retrieve`, { method: "POST", body: JSON.stringify({ slug, q, top_k }) }),
  salaryBook: (limit = 50) => _fetch(`${SAGE}/ledger/salary-book?limit=${limit}`),
  salaryEntryAdd: (payload) => _fetch(`${SAGE}/admin/salary/entry`, { method: "POST", auth: true, body: JSON.stringify(payload) }),
  replyWall: (limit = 25) => _fetch(`${SAGE}/ledger/reply-wall?limit=${limit}`),
  replyWallAdd: (payload) => _fetch(`${SAGE}/admin/reply-wall/entry`, { method: "POST", auth: true, body: JSON.stringify(payload) }),
  handshake: () => _fetch(`${SAGE}/handshake`),
  consensus: (message, system_prompt) => _fetch(`${SAGE}/chat/consensus`, { method: "POST", auth: true, body: JSON.stringify({ message, system_prompt: system_prompt || null }) }),
  consensusLedger: (limit = 20) => _fetch(`${SAGE}/ledger/consensus?limit=${limit}`),
  bountyHunterToggle: (value, reason) => _fetch(`${SAGE}/admin/bounty-hunter`, { method: "POST", auth: true, body: JSON.stringify({ value, reason }) }),
  fundTheGateToggle: (value, reason) => _fetch(`${SAGE}/admin/fund-the-gate`, { method: "POST", auth: true, body: JSON.stringify({ value, reason }) }),
  products: () => _fetch(`${SAGE}/products`),
  manual: () => _fetch(`${SAGE}/manual`),
  manualChapter: (key) => _fetch(`${SAGE}/manual/${key}`),
  firstDollarCert: () => _fetch(`${SAGE}/certificates/first-dollar`),
  flags: () => _fetch(`${SAGE}/flags`),
  waitlist: (email, source) => _fetch(`${SAGE}/waitlist`, { method: "POST", body: JSON.stringify({ email, source }) }),
  openGate: () => _fetch(`${SAGE}/open-gate`),
  openGateSignup: (email, payout_method) => _fetch(`${SAGE}/open-gate/signup`, { method: "POST", body: JSON.stringify({ email, payout_method }) }),
  gateFundInterest: (email, amount_usd, note) => _fetch(`${SAGE}/gate/fund-interest`, { method: "POST", body: JSON.stringify({ email, amount_usd, note }) }),
  philanthropy: () => _fetch(`${SAGE}/philanthropy`),
  sentenceVote: (sentence_id, vote, client_token) => _fetch(`${SAGE}/sentences/vote`, { method: "POST", body: JSON.stringify({ sentence_id, vote, client_token }) }),
  estimate: (message, depth) => _fetch(`${SAGE}/estimate`, { method: "POST", auth: true, body: JSON.stringify({ message, depth }) }),
  sessions: () => _fetch(`${SAGE}/sessions`, { auth: true }),
  session: (sid) => _fetch(`${SAGE}/session/${sid}`, { auth: true }),
  deleteSession: (sid) => _fetch(`${SAGE}/session/${sid}`, { method: "DELETE", auth: true }),
  rate: (session_id, turn_id, label, note) => _fetch(`${SAGE}/rate`, { method: "POST", auth: true, body: JSON.stringify({ session_id, turn_id, label, note }) }),
  retract: (session_id, turn_id, note) => _fetch(`${SAGE}/retract`, { method: "POST", auth: true, body: JSON.stringify({ session_id, turn_id, note }) }),
  refund: (session_id, turn_id) => _fetch(`${SAGE}/refund`, { method: "POST", auth: true, body: JSON.stringify({ session_id, turn_id }) }),
  handoff: (session_id, turn_id, tool_name, reason) => _fetch(`${SAGE}/handoff`, { method: "POST", auth: true, body: JSON.stringify({ session_id, turn_id, tool_name, reason }) }),
  speak: (text) => _fetch(`${SAGE}/chat/speak`, { method: "POST", body: JSON.stringify({ text }) }),
  coldRead: {
    prompts: () => _fetch(`${SAGE}/cold-read/prompts`),
    latest: () => _fetch(`${SAGE}/cold-read/latest`),
    run: (reason) => _fetch(`${SAGE}/admin/cold-read/run`, { method: "POST", auth: true, body: JSON.stringify({ reason }) }),
  },
  export: () => _fetch(`${SAGE}/export`, { auth: true }),
  seed: {
    get: () => _fetch(`${SAGE}/seed`, { auth: true }),
    put: (content) => _fetch(`${SAGE}/seed`, { method: "PUT", auth: true, body: JSON.stringify({ content }) }),
  },
  admin: {
    status: () => _fetch(`${SAGE}/admin/status`, { auth: true }),
    launch: (value, reason) => _fetch(`${SAGE}/admin/launch`, { method: "POST", auth: true, body: JSON.stringify({ value, reason }) }),
    killSwitch: (value, reason) => _fetch(`${SAGE}/admin/kill-switch`, { method: "POST", auth: true, body: JSON.stringify({ value, reason }) }),
    deployFreeze: (value, reason) => _fetch(`${SAGE}/admin/deploy-freeze`, { method: "POST", auth: true, body: JSON.stringify({ value, reason }) }),
    fundTheGate: (value, reason) => _fetch(`${SAGE}/admin/fund-the-gate`, { method: "POST", auth: true, body: JSON.stringify({ value, reason }) }),
    rebateCheck: (reason) => _fetch(`${SAGE}/admin/rebate-streak/check`, { method: "POST", auth: true, body: JSON.stringify({ reason }) }),
    modelOpennessRun: (model, reason) => _fetch(`${SAGE}/admin/model-openness/run`, { method: "POST", auth: true, body: JSON.stringify({ model, reason }) }),
    witnessLog: () => _fetch(`${SAGE}/admin/witness-log`, { auth: true }),
    personaDiff: (old_line, new_line, reason) => _fetch(`${SAGE}/admin/persona-diff`, { method: "POST", auth: true, body: JSON.stringify({ old_line, new_line, reason }) }),
    gateEntry: (payload) => _fetch(`${SAGE}/admin/gate/entry`, { method: "POST", auth: true, body: JSON.stringify(payload) }),
    philEvent: (payload) => _fetch(`${SAGE}/admin/philanthropy/event`, { method: "POST", auth: true, body: JSON.stringify(payload) }),
  },
  personaScrolls: {
    createDraft: (persona_slug, scroll_type, title, text) =>
      _fetch(`${SAGE}/persona-scrolls/draft`, {
        method: "POST",
        auth: true,
        body: JSON.stringify({ persona_slug, scroll_type, title, text }),
      }),
    listDrafts: (persona_slug, scroll_type) => {
      const q = new URLSearchParams();
      if (persona_slug) q.set("persona_slug", persona_slug);
      if (scroll_type) q.set("scroll_type", scroll_type);
      const qs = q.toString();
      return _fetch(`${SAGE}/persona-scrolls/drafts${qs ? "?" + qs : ""}`, { auth: true });
    },
    getDraft: (id) => _fetch(`${SAGE}/persona-scrolls/draft/${id}`, { auth: true }),
    editDraft: (id, text, title) =>
      _fetch(`${SAGE}/persona-scrolls/draft/${id}`, {
        method: "PUT",
        auth: true,
        body: JSON.stringify(title !== undefined && title !== null ? { text, title } : { text }),
      }),
  },
  loreVisibility: {
    get: () => _fetch(`${SAGE}/lore-visibility`),
    set: (flags, reason) =>
      _fetch(`${SAGE}/lore-visibility`, {
        method: "PUT",
        auth: true,
        body: JSON.stringify({ ...flags, reason }),
      }),
  },
  // v1.37 — launch-readiness wave
  coldStart: () => _fetch(`${SAGE}/cold-start`),
  coldStartConfig: (patch) =>
    _fetch(`${SAGE}/admin/cold-start/config`, {
      method: "PUT",
      auth: true,
      body: JSON.stringify(patch),
    }),
  onboarding: {
    register: (email, display_name) =>
      _fetch(`${SAGE}/onboarding/register`, {
        method: "POST",
        body: JSON.stringify({ email, display_name }),
      }),
    quota: (email) =>
      _fetch(`${SAGE}/onboarding/quota?email=${encodeURIComponent(email)}`),
    consume: (email, cost_usd) =>
      _fetch(`${SAGE}/onboarding/consume`, {
        method: "POST",
        body: JSON.stringify({ email, cost_usd }),
      }),
  },
  bundles: () => _fetch(`${SAGE}/bundles`),
  bundlesConfig: (items) =>
    _fetch(`${SAGE}/admin/bundles`, {
      method: "PUT",
      auth: true,
      body: JSON.stringify({ items }),
    }),
  trial: {
    start: (email) =>
      _fetch(`${SAGE}/trial/start`, {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    status: (email) =>
      _fetch(`${SAGE}/trial/status?email=${encodeURIComponent(email)}`),
  },
  contest: {
    current: () => _fetch(`${SAGE}/contests/current`),
    set: (patch) =>
      _fetch(`${SAGE}/admin/contests/current`, {
        method: "PUT",
        auth: true,
        body: JSON.stringify(patch),
      }),
  },
  memoryAnchor: {
    get: () => _fetch(`${SAGE}/memory/anchor`, { auth: true }),
    refresh: () =>
      _fetch(`${SAGE}/memory/anchor/refresh`, { method: "POST", auth: true }),
  },
  supportSubmit: ({ issue, session_id, email }) =>
    _fetch(`${SAGE}/support/submit`, {
      method: "POST",
      body: JSON.stringify({ issue, session_id, email }),
    }),
};

// Client-side sha256 (Web Crypto). Used only for the pre-save preview so
// the Founder can eyeball a match against the server-returned sha256.
// The server-computed sha256 remains the source of truth.
export async function sha256Hex(text) {
  const enc = new TextEncoder().encode(text || "");
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function* chatStream({ message, depth, security, session_id, search, attachments }) {
  const res = await fetch(`${SAGE}/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${getToken()}`,
    },
    body: JSON.stringify({ message, depth, security, session_id, search: !!search, attachments: attachments || [] }),
  });
  if (!res.ok) {
    let msg = "";
    try { msg = (await res.json()).detail || ""; } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const chunks = buf.split("\n\n");
    buf = chunks.pop() || "";
    for (const chunk of chunks) {
      const lines = chunk.split("\n");
      let evName = "message";
      let data = "";
      for (const l of lines) {
        if (l.startsWith("event: ")) evName = l.slice(7).trim();
        else if (l.startsWith("data: ")) data += l.slice(6);
      }
      if (!data) continue;
      try {
        yield { event: evName, data: JSON.parse(data) };
      } catch {
        yield { event: evName, data };
      }
    }
  }
}

export function newClientToken() {
  let t = localStorage.getItem("sage_client_token");
  if (!t) {
    t = crypto.randomUUID();
    localStorage.setItem("sage_client_token", t);
  }
  return t;
}
